import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

/**
 * Recognises the native "the fetch itself failed" `TypeError` a browser/WebView
 * raises when a request can't leave the machine (offline, DNS failure, dropped
 * connection). WKWebView (desktop) uses `Load failed`; Chromium/Firefox use
 * `Failed to fetch` / `NetworkError when attempting to fetch resource`.
 *
 * These are distinct from HTTP error responses (a 4xx/5xx resolves the fetch —
 * it does not reject), so matching this shape only ever catches transport-level
 * failures, not application errors.
 */
const NETWORK_FAILURE_MESSAGE =
  /load failed|failed to fetch|networkerror|network request failed/i;

const isFetchTransportFailure = (reason: Error): boolean =>
  reason.name === 'TypeError' && NETWORK_FAILURE_MESSAGE.test(reason.message);

/**
 * Last-resort fallback for an axios-shaped error that arrives with no `code`.
 *
 * This is the second spelling of `'Network Error'` in the tree — `desktopOfflineNetwork.ts`
 * holds the first as `AXIOS_NETWORK_ERROR_MESSAGE`. Deliberately not shared: that
 * module pulls in jotai, the offline-recording atoms and the Tauri event bridge,
 * and this guard installs in the earliest client boot, so importing a string from
 * it would drag that whole graph onto the boot path to deduplicate eleven
 * characters. Kept as a regex rather than an equality check because, unlike the
 * XHR layer's, this one also has to tolerate a re-thrower's casing.
 */
const AXIOS_TRANSPORT_FAILURE_MESSAGE = /^network error$/i;

type AxiosLikeError = Error & { code?: unknown; response?: unknown };

/**
 * Axios's adapter catches the transport `TypeError` and re-throws its own
 * `AxiosError`, so the match above never sees it. An absent `response` is the
 * load-bearing half: an `AxiosError` *with* one is an HTTP status rejection the
 * server actually answered — an application error that must keep surfacing.
 *
 * @see `isNoResponseNetworkFailure` in `src/utils/http/desktopOfflineNetwork.ts`,
 * which answers a similar question for the outbound-XHR layer. This predicate is
 * deliberately **narrower**, because the two exist for opposite purposes: that one
 * *classifies* a failure the app is already handling (widening it shows the user a
 * friendlier message), whereas this one *deletes* a rejection from Datadog RUM and
 * from `installRejectionShapeProbe` (widening it destroys signal). Hence three
 * divergences, all intentional: `ECONNABORTED` is excluded (a timeout is a real
 * failure worth reporting), `request != null` is not consulted (it matches any
 * dispatched request, cancellations included), and `name === 'AxiosError'` is
 * required (it excludes `CanceledError`, which is otherwise shape-identical).
 *
 * Reusing that module's exported entry point would also be wrong mechanically:
 * `isDesktopOfflineNetworkError` has side effects by design — it arms the
 * connectivity discredit window and wakes the Rust probe — and this handler runs on
 * every unhandled rejection at boot. Widen either predicate without reading the
 * other and one of those two properties breaks silently.
 */
const isAxiosTransportFailure = (reason: Error): boolean => {
  if (reason.name !== 'AxiosError') return false;
  const { code, response } = reason as AxiosLikeError;
  if (response !== undefined && response !== null) return false;
  return code === 'ERR_NETWORK' || AXIOS_TRANSPORT_FAILURE_MESSAGE.test(reason.message);
};

/**
 * Datadog tag for a suppressed rejection, following the convention in
 * `src/utils/http/desktopOfflineNetwork.ts`.
 *
 * This handler deletes rejections from every other sink — RUM and the shape probe
 * both sit behind the `stopImmediatePropagation()` — so this log line is the only
 * evidence a suppression happened. A stable tag makes it a dashboard facet rather
 * than a free-text search, which is what makes two questions answerable after
 * ship: how much this guard is suppressing at all, and whether `shape="axios"` is
 * growing — the signature of an app request being silently swallowed, per the
 * caveat on {@link handleOfflineNetworkRejection}.
 */
const TAG_SUPPRESSED = 'tag="offline_rejection_suppressed"';

/** Which transport produced the failure, or `null` if this is not one. */
type NetworkFailureShape = 'fetch' | 'axios';

const classifyNetworkFailureRejection = (reason: unknown): NetworkFailureShape | null => {
  if (!(reason instanceof Error)) return null;
  if (isFetchTransportFailure(reason)) return 'fetch';
  if (isAxiosTransportFailure(reason)) return 'axios';
  return null;
};

/**
 * Handles a global `unhandledrejection` from a **fire-and-forget** network call.
 *
 * The desktop analytics SDKs (PostHog `session_recording`, Amplitude Session
 * Replay, periodic event/flag flushes) stream to `sys.heidihealth.com` in the
 * background with no `.catch()`. The moment one is in flight while the machine
 * is offline, the WebView `fetch` rejects with `TypeError: Load failed` and it
 * becomes an unhandled rejection — surfacing as the full-screen Next.js dev
 * overlay and, in packaged builds, as Datadog error noise every time the user
 * is offline.
 *
 * We only swallow the **transport-failure** shapes — the native `TypeError` and
 * the equivalent `AxiosError` an axios-based SDK re-throws in its place.
 *
 * For the raw `fetch` `TypeError` that is definitionally a fire-and-forget
 * telemetry upload: the analytics SDKs are the only things in the tree using
 * bare `fetch`, and app requests go through TanStack Query, which owns its own
 * rejections.
 *
 * **The axios shape is weaker and should not be read as the same claim.** The
 * accurate claim for it is not "by definition telemetry" but "by definition not
 * owned by TanStack Query" — it keys on transport shape, not on who made the
 * request, so it matches **any** axios instance in the tree (`globalHTTP`, the
 * `packages/api` scribe clients, `formsApi`, `packages/coding`). If you are
 * adding a fire-and-forget axios call, know that this guard is in its path:
 * give it an explicit `.catch()`, as today's fire-and-forget sites all do, or
 * its failures will be suppressed here rather than surfacing in Datadog.
 *
 * Axios *is* the app's own transport (`scribeHTTP`, `t11nClient`), and
 * `attachConfirmedDesktopOfflineRequestShortCircuit` manufactures exactly this
 * shape on those clients, so an app request can reach here. Every such call site
 * is today awaited inside `try`/`catch` or owned by TanStack Query, so nothing is
 * over-suppressed — but that holds by call-site discipline, not by construction.
 * It is suppressed because it is a transport failure regardless of caller: there
 * is no user-actionable difference between an offline SDK upload and an offline
 * app request, and the app request's own error path has already run.
 *
 * The cost if that discipline ever lapses is silent, which is why the log line
 * below carries a stable tag: a `void`-ed app request added later would be
 * suppressed here with no other signal, since `stopImmediatePropagation()` removes
 * the rejection from Datadog RUM *and* from `installRejectionShapeProbe`. Watch
 * {@link TAG_SUPPRESSED} with `shape="axios"` for that number growing.
 *
 * Every suppressed rejection is still `logger.info`-logged — never a silent
 * swallow — so the signal is retained for diagnosis (info is forwarded to Datadog
 * in packaged prod builds, unlike debug). Non-network rejections are left
 * untouched and still surface.
 */
export const handleOfflineNetworkRejection = (event: PromiseRejectionEvent): void => {
  const shape = classifyNetworkFailureRejection(event.reason);
  if (shape === null) return;
  // `preventDefault()` alone only cancels the console "Uncaught (in promise)"
  // default — it does NOT stop the other `unhandledrejection` listeners the
  // Next.js dev overlay and Datadog RUM register. Because this event is
  // dispatched at `window` (the target), listeners fire in registration order,
  // and we install before `initDatadog`/the overlay (see install), so we run
  // first and `stopImmediatePropagation()` stops those later listeners from
  // seeing this fire-and-forget network failure: no dev overlay, no Datadog
  // error. Both `preventDefault()` and `stopImmediatePropagation()` are needed.
  event.preventDefault();
  event.stopImmediatePropagation();
  const reason = event.reason as AxiosLikeError;
  logger.info(
    `[offline-network-rejection-guard] suppressed fire-and-forget network rejection ${TAG_SUPPRESSED}`,
    {
      shape,
      name: reason.name,
      // The field the axios half of the predicate actually keys off, and a closed
      // value space, so it says WHICH branch fired: a suppression with no `code`
      // reached the message-regex fallback, meaning something re-threw an
      // axios-shaped error without one. `name` + `message` alone cannot separate
      // those two, and the fallback is the one worth knowing is carrying load.
      code: typeof reason.code === 'string' ? reason.code : undefined,
      // Free-form, and unconstrained on the `ERR_NETWORK` branch — that check
      // short-circuits before the message regex, so whatever the thrower put here
      // is forwarded verbatim to Datadog. In practice it is a constant: axios only
      // ever pairs `ERR_NETWORK` with the literal `'Network Error'`, and
      // `createDesktopOfflineRequestError` uses the same literal. Kept because it
      // is the only thing separating `Load failed` from `Failed to fetch` on the
      // fetch branch. Nothing else about the request — URL, body, headers — may be
      // added here; `code` and `shape` are the fields to reach for instead.
      message: reason.message,
    }
  );
};

/**
 * Installs {@link handleOfflineNetworkRejection} as a `window` `unhandledrejection`
 * listener. Desktop-only (`isTauri()`) — this targets the desktop offline
 * experience; web app fetches are handled by TanStack Query and web has no dev
 * overlay in production.
 *
 * Install this as early as possible in client boot so it's registered ahead of
 * the Next.js dev overlay and Datadog listeners — for this `window`-target event
 * that registration order is what guarantees we run first (and can
 * `stopImmediatePropagation()` before them). The **capture** phase is used
 * defensively in case a sink ever registers on a node higher in the propagation
 * path. Returns a cleanup that removes the listener.
 */
export const installOfflineNetworkRejectionGuard = (): (() => void) => {
  if (typeof window === 'undefined' || !isTauri()) return () => undefined;
  window.addEventListener('unhandledrejection', handleOfflineNetworkRejection, true);
  return () =>
    window.removeEventListener('unhandledrejection', handleOfflineNetworkRejection, true);
};
